import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, errorText, fmtTime } from "@/lib/api";

const PILL = { active: "text-emerald-300 border-emerald-500/40", pending: "text-amber-300 border-amber-500/40", canceled: "text-rose-300 border-rose-500/40",
  paid: "text-emerald-300 border-emerald-500/40", due: "text-amber-300 border-amber-500/40", failed: "text-rose-300 border-rose-500/40",
  installed: "text-emerald-300 border-emerald-500/40", awaiting_install: "text-slate-300 border-slate-500/40" };
const LABEL = { active: "Paid", pending: "Awaiting payment", canceled: "Canceled", paid: "Paid", due: "$200 due at install", failed: "Charge failed", installed: "Installed", awaiting_install: "Awaiting install", completing: "Completing…" };

const Pill = ({ v, testid }) => <span className={`inline-block rounded border px-2 py-0.5 font-mono text-[10px] whitespace-nowrap ${PILL[v] || "text-slate-400 border-slate-600"}`} data-testid={testid}>{LABEL[v] || v}</span>;

export const PropertiesTable = ({ rows, basePath, onChange, showMsp = false }) => {
  const [confirm, setConfirm] = useState(null);
  const [busy, setBusy] = useState(false);

  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${basePath}/${confirm.id}/install-complete`);
      toast.success(data.lease_balance_status === "paid" && confirm.plan === "lease" ? "Install complete. $200 lease balance charged." : "Install marked complete.");
      onChange();
    } catch (e) { toast.error(errorText(e)); onChange(); }
    setBusy(false);
    setConfirm(null);
  };

  if (!rows.length) return <p className="rounded-md border border-dashed border-[#222D3D] p-8 text-center text-sm text-slate-500" data-testid="properties-empty">No properties have signed up yet.</p>;
  const heads = ["Property", ...(showMsp ? ["MSP"] : []), "Plan", "Payment", "Lease balance", "Install", ""];
  return (
    <div className="rounded-md border border-[#222D3D] bg-[#0F141C] overflow-x-auto" data-testid="properties-table">
      <Table>
        <TableHeader><TableRow className="border-[#222D3D] hover:bg-transparent">
          {heads.map((h, i) => <TableHead key={i} className="text-[11px] uppercase tracking-[0.14em] text-slate-500">{h}</TableHead>)}
        </TableRow></TableHeader>
        <TableBody>
          {rows.map((p) => (
            <TableRow key={p.id} className="border-[#222D3D] hover:bg-[#141A24]" data-testid={`property-row-${p.email}`}>
              <TableCell><p className="text-slate-100">{p.property_name}</p><p className="text-xs text-slate-500">{p.property_address} · {p.email} · {fmtTime(p.created_at)}</p><p className="font-mono text-[10px] text-slate-600" data-testid={`property-id-${p.email}`}>ID {p.id}</p></TableCell>
              {showMsp && <TableCell className="text-sm text-slate-300">{p.msp_name || "Direct"}</TableCell>}
              <TableCell className="text-sm text-slate-300 whitespace-nowrap">{p.plan === "lease" ? "Lease purchase" : "Purchase"}</TableCell>
              <TableCell><Pill v={p.billing_status} testid={`property-billing-${p.email}`} /></TableCell>
              <TableCell>{p.plan === "lease" ? <Pill v={p.lease_balance_status || "due"} testid={`property-lease-${p.email}`} /> : <span className="text-xs text-slate-600">—</span>}</TableCell>
              <TableCell><Pill v={p.install_status} testid={`property-install-${p.email}`} /></TableCell>
              <TableCell className="text-right">
                {!["installed", "completing"].includes(p.install_status) && (
                  <Button size="sm" variant="outline" disabled={p.billing_status !== "active"} onClick={() => setConfirm(p)} className="border-[#222D3D] bg-transparent" data-testid={`property-complete-btn-${p.email}`}>Mark install complete</Button>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0F141C] border-[#222D3D]" data-testid="install-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Mark {confirm?.property_name} installed?</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.plan === "lease" ? "The system must be installed and operating on the property's network. The property's card will be charged the $200 lease balance now." : "Confirm the system is installed and operating on the property's network."}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="install-confirm-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); complete(); }} disabled={busy} className="bg-emerald-500 hover:bg-emerald-400 text-[#07090E]" data-testid="install-confirm-ok">{confirm?.plan === "lease" ? "Confirm and charge $200" : "Confirm"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
